import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useDashboard, usePuzzle } from "../api/queries";
import { SHARE_CHANNELS, type Dashboard } from "../api/schemas";
import { ScoreHistogram, Sparkline } from "../components/charts/charts";
import { tableStyles } from "../components/table";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  KpiTile,
  Loading,
  PageHeader,
  Skeleton,
  Stat,
  StatusChip,
  TextInput,
} from "../components/ui";
import { addDays, formatLongDate, formatShortDate, todayUtc } from "../lib/dates";
import { fmtCompact, fmtInt, fmtMs, fmtPct, typeSlug } from "../lib/format";
import { useHotkey } from "../lib/hotkeys";
import { PuzzleView } from "@lumen/puzzle-render";
import styles from "./pages.module.css";

export function DashboardPage() {
  const [params, setParams] = useSearchParams();
  const today = todayUtc();
  const date = params.get("date") || today;
  const q = useDashboard(date === today ? undefined : date);
  const setDate = (d: string) => setParams(d && d !== today ? { date: d } : {}, { replace: true });

  const header = (
    <PageHeader
      title={date === today ? "Today" : formatLongDate(date)}
      meta={
        <span className="mono">
          {[`${formatLongDate(date)} UTC`, q.data?.puzzleNumber ? `puzzle #${q.data.puzzleNumber}` : null, q.data?.type]
            .filter(Boolean)
            .join("  ·  ")}
        </span>
      }
      actions={
        <>
          <Button size="sm" variant="ghost" onClick={() => setDate(addDays(date, -1))} aria-label="Previous day">
            ←
          </Button>
          <TextInput type="date" value={date} max={addDays(today, 90)} onChange={(e) => setDate(e.target.value)} aria-label="Dashboard date" />
          <Button size="sm" variant="ghost" onClick={() => setDate(addDays(date, 1))} aria-label="Next day">
            →
          </Button>
          {date !== today ? (
            <Button size="sm" onClick={() => setDate(today)}>
              Today
            </Button>
          ) : null}
        </>
      }
    />
  );

  if (q.isPending) {
    return (
      <>
        {header}
        <div className={styles.kpiRow}>
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} height={164} radius="30px" />
          ))}
        </div>
        <Loading rows={8} />
      </>
    );
  }
  if (q.isError) {
    return (
      <>
        {header}
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </>
    );
  }

  const d = q.data;
  const live = d.daily.submitted > 0;
  const completion = d.daily.started ? d.daily.submitted / d.daily.started : null;

  return (
    <>
      {header}
      <div className={styles.kpiRow}>
        <KpiTile
          palette="violet"
          label={
            <>
              Players <small>total</small>
            </>
          }
          value={String(d.players.total)}
          foot={
            <>
              <span>+{fmtInt(d.players.newToday)} new</span>
              <span className="mono">{fmtCompact(d.streaks.active)} on a streak</span>
            </>
          }
        />
        <KpiTile
          palette="azure"
          label={
            <>
              Daily <small>submitted</small>
            </>
          }
          value={String(d.daily.submitted)}
          foot={
            <>
              <span>of {fmtInt(d.daily.started)} started</span>
              <span className="mono">{fmtPct(completion)}</span>
            </>
          }
        />
        <KpiTile
          palette="teal"
          label={
            <>
              Correct <small>rate</small>
            </>
          }
          value={d.daily.correctRate === null ? "-" : String(Math.round(d.daily.correctRate * 100))}
          unit="%"
          foot={
            <>
              <span>median solve</span>
              <span className="mono">{fmtMs(d.daily.medianElapsedMs)}</span>
            </>
          }
        />
        <KpiTile
          palette="ember"
          href={`/integrity?date=${d.date}`}
          label={
            <>
              Flagged <small>submissions</small>
            </>
          }
          value={String(d.daily.flagged)}
          foot={
            <>
              <span>too fast · clock mismatch</span>
              <span className="mono">Integrity →</span>
            </>
          }
        />
      </div>

      <div className={styles.statStrip}>
        <Stat label="Levels completed" value={fmtInt(d.levels.completionsToday)} sub="today" />
        <Stat label="Level players" value={fmtInt(d.levels.activePlayers)} sub="active today" />
        <Stat label="Active streaks" value={fmtInt(d.streaks.active)} />
        <Stat label="Longest streak" value={`${fmtInt(d.streaks.longest)}d`} />
        <Stat label="Started" value={fmtInt(d.daily.started)} sub="daily" />
        <Stat label="Shares" value={d.shares ? fmtInt(d.shares.today) : "—"} sub={d.shares ? "created on this day" : "needs the Phase 1 API"} />
        <Stat label="Push devices" value={d.devices ? fmtInt(d.devices.withPush) : "—"} sub="installs with an APNs token" />
      </div>

      <div className={styles.dashGrid}>
        <TodayPuzzleCard puzzleId={d.puzzleId} />
        <div className={styles.dashRight}>
          <Card
            title="Score distribution"
            actions={<span className="monoLabel">50 bins · 0–1000</span>}
          >
            {live ? (
              <ScoreHistogram bins={d.daily.bins} />
            ) : (
              <EmptyState title="No submissions yet">
                {d.puzzleId ? "The distribution fills in once players submit this day's puzzle." : "No puzzle is scheduled for this day."}
              </EmptyState>
            )}
          </Card>
          <SharesCard shares={d.shares} />
          <Last7Card rows={d.last7} />
        </div>
      </div>
    </>
  );
}

function TodayPuzzleCard({ puzzleId }: { puzzleId: string | null }) {
  const q = usePuzzle(puzzleId ?? undefined);
  const [reveal, setReveal] = useState(false);
  useHotkey("r", () => setReveal((v) => !v), Boolean(q.data));
  return (
    <Card
      title="Puzzle of the day"
      className={styles.previewCard}
      actions={
        q.data ? (
          <>
            <StatusChip status={q.data.puzzle.status} />
            <Button size="sm" variant={reveal ? "primary" : "secondary"} onClick={() => setReveal((v) => !v)} shortcut="R" aria-pressed={reveal}>
              {reveal ? "Hide" : "Reveal"}
            </Button>
          </>
        ) : null
      }
    >
      {!puzzleId ? (
        <EmptyState title="Nothing scheduled">Pick another day or fill the schedule.</EmptyState>
      ) : q.isPending ? (
        <Loading rows={6} />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <div className={styles.previewMeta}>
            <Link to={`/puzzles/${encodeURIComponent(q.data.puzzle.id)}`} className="mono">
              {q.data.puzzle.id} →
            </Link>
            <span className="mono">
              {typeSlug(q.data.puzzle.type)} · tier {q.data.puzzle.spec.difficulty.tier ?? "—"}
            </span>
          </div>
          <div className={styles.previewStage}>
            <PuzzleView spec={q.data.puzzle.spec} answer={q.data.puzzle.answer} reveal={reveal} compact />
          </div>
        </>
      )}
    </Card>
  );
}

const CHANNEL_LABEL: Record<string, string> = {
  copy: "Copy",
  messages: "Messages",
  whatsapp: "WhatsApp",
  instagram: "Instagram",
  tiktok: "TikTok",
  x: "X",
  other: "Other",
};

/** Shares created on the day, by channel (POST /v1/daily/{n}/share). */
function SharesCard({ shares }: { shares: Dashboard["shares"] }) {
  const rows = shares
    ? [...new Set([...SHARE_CHANNELS, ...Object.keys(shares.byChannel)])].map((ch) => ({ ch, n: shares.byChannel[ch] ?? 0 }))
    : [];
  const max = Math.max(1, ...rows.map((r) => r.n));
  return (
    <Card title="Shares by channel" actions={shares ? <span className="mono">{fmtInt(shares.today)} today</span> : null}>
      {!shares ? (
        <EmptyState title="No share data">This backend doesn't report shares yet (contracts/phase1.md, Admin additions).</EmptyState>
      ) : shares.today === 0 ? (
        <EmptyState title="No shares yet">Share cards created on this day show up here by channel.</EmptyState>
      ) : (
        <div className={styles.progressList} data-testid="shares-by-channel">
          {rows.map((r) => (
            <div key={r.ch} className={styles.progressRow}>
              <span className={styles.channel}>{CHANNEL_LABEL[r.ch] ?? r.ch}</span>
              <span className={styles.meter} data-tone="accent">
                <span style={{ width: `${(r.n / max) * 100}%` }} />
              </span>
              <span className="mono">{fmtInt(r.n)}</span>
              <span className={`mono ${styles.faint}`}>{shares.today ? `${Math.round((r.n / shares.today) * 100)}%` : ""}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function Last7Card({ rows }: { rows: { date: string; submitted: number; correctRate: number | null }[] }) {
  return (
    <Card
      title="Last 7 days"
      padded={false}
      actions={
        <div className={styles.sparkPair}>
          <span>
            <span className="monoLabel">submitted</span>
            <Sparkline values={rows.map((r) => r.submitted || null)} label="Submitted over the last 7 days" />
          </span>
          <span>
            <span className="monoLabel">correct</span>
            <Sparkline values={rows.map((r) => r.correctRate)} label="Correct rate over the last 7 days" />
          </span>
        </div>
      }
    >
      <table className={tableStyles.table}>
        <thead>
          <tr>
            <th>Date</th>
            <th data-align="right">Submitted</th>
            <th data-align="right">Correct</th>
            <th aria-label="Correct rate bar" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.date}>
              <td>
                <Link to={`/?date=${r.date}`} className={tableStyles.mono}>
                  {formatShortDate(r.date)}
                </Link>
              </td>
              <td data-align="right" className="mono">
                {r.submitted ? fmtInt(r.submitted) : <span className={tableStyles.faint}>—</span>}
              </td>
              <td data-align="right" className="mono">
                {fmtPct(r.correctRate)}
              </td>
              <td style={{ width: "40%" }}>
                <span className={styles.meter}>
                  <span style={{ width: `${(r.correctRate ?? 0) * 100}%` }} />
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
